import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Typography,
  IconButton,
  InputAdornment,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Switch,
  Box,
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import { Colorize } from "@material-ui/icons";
import Checkbox from "@mui/material/Checkbox";
import { isArray } from "lodash";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import SchedulesForm from "../SchedulesForm";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import ColorBoxModal from "../ColorBoxModal";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  colorAdorment: {
    width: 24,
    height: 24,
    borderRadius: 4,
    border: "1px solid rgba(255, 255, 255, 0.2)",
  },
  rotationBox: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.03)"
        : "rgba(0, 0, 0, 0.02)",
  },
  rotationTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
  },
}));

const QueueSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, i18n.t("queueModal.validations.tooShort"))
    .max(50, i18n.t("queueModal.validations.tooLong"))
    .required(i18n.t("queueModal.validations.required")),
  color: Yup.string()
    .min(3, i18n.t("queueModal.validations.tooShort"))
    .max(9, i18n.t("queueModal.validations.tooLong"))
    .required(i18n.t("queueModal.validations.required")),
  outOfHoursMessage: Yup.string(),
});

const QueueModal = ({ open, onClose, queueId, onEdit }) => {
  const classes = useStyles();

  const initialState = {
    name: "",
    color: "#005c53",
    outOfHoursMessage: "",
    tempoRoteador: 0,
    ativarRoteador: false,
    typeRandomMode: "RANDOM",
    randomizeImmediate: false,
  };

  const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);
  const [queue, setQueue] = useState(initialState);
  const [schedulesEnabled, setSchedulesEnabled] = useState(false);
  const [tab, setTab] = useState(0);

  const initialStateSchedule = [
    { weekday: i18n.t("queueModal.serviceHours.monday"), weekdayEn: "monday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00" },
    { weekday: i18n.t("queueModal.serviceHours.tuesday"), weekdayEn: "tuesday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00" },
    { weekday: i18n.t("queueModal.serviceHours.wednesday"), weekdayEn: "wednesday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00" },
    { weekday: i18n.t("queueModal.serviceHours.thursday"), weekdayEn: "thursday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00" },
    { weekday: i18n.t("queueModal.serviceHours.friday"), weekdayEn: "friday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00" },
    { weekday: i18n.t("queueModal.serviceHours.saturday"), weekdayEn: "saturday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00" },
    { weekday: i18n.t("queueModal.serviceHours.sunday"), weekdayEn: "sunday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00" }
  ];

  const [schedules, setSchedules] = useState(initialStateSchedule);
  const { get: getSetting } = useCompanySettings();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const setting = await getSetting({ column: "scheduleType" });
        if (setting?.scheduleType === "queue") {
          setSchedulesEnabled(true);
        }
      } catch (err) {
        console.error("Error fetching schedule settings:", err);
      }
    };
    fetchSettings();
  }, [getSetting]);

  useEffect(() => {
    (async () => {
      if (!queueId) {
        setQueue(initialState);
        setSchedules(initialStateSchedule);
        return;
      }
      try {
        const { data } = await api.get(`/queue/${queueId}`);
        setQueue((prevState) => ({
          ...prevState,
          ...data,
          tempoRoteador: data.tempoRoteador ?? 0,
          ativarRoteador: !!data.ativarRoteador,
          typeRandomMode: data.typeRandomMode || "RANDOM",
          randomizeImmediate: !!data.randomizeImmediate,
        }));

        if (isArray(data.schedules) && data.schedules.length > 0) {
          setSchedules(data.schedules);
        }
      } catch (err) {
        toastError(err);
      }
    })();

    return () => {
      setQueue(initialState);
      setTab(0);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueId, open]);

  const handleClose = () => {
    onClose();
    setTab(0);
  };

  const handleSaveSchedules = async (values) => {
    toast.success(i18n.t("common.toasts.clickToSave"));
    setSchedules(values);
    setTab(0);
  };

  const handleSaveQueue = async (values) => {
    try {
      if (queueId) {
        await api.put(`/queue/${queueId}`, { ...values, schedules });
      } else {
        await api.post("/queue", { ...values, schedules });
      }

      toast.success(`${i18n.t("queues.toasts.success")}`);
      if (onEdit) {
        onEdit();
      }
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        maxWidth="md"
        fullWidth
        open={open}
        onClose={handleClose}
        scroll="paper"
      >
        <DialogTitle>
          {queueId
            ? `${i18n.t("queueModal.title.edit")}`
            : `${i18n.t("queueModal.title.add")}`}
        </DialogTitle>
        <Tabs
          value={tab}
          indicatorColor="primary"
          textColor="primary"
          onChange={(e, v) => setTab(v)}
          aria-label="queue tabs"
        >
          <Tab label={i18n.t("queueModal.title.queueData")} />
          {schedulesEnabled && (
            <Tab label={i18n.t("queueModal.title.schedules") || i18n.t("queueModal.title.text")} />
          )}
        </Tabs>

        {tab === 0 && (
          <Formik
            initialValues={queue}
            validateOnChange={false}
            enableReinitialize={true}
            validationSchema={QueueSchema}
            onSubmit={(values, actions) => {
              setTimeout(() => {
                handleSaveQueue(values);
                actions.setSubmitting(false);
              }, 400);
            }}
          >
            {({ setFieldValue, touched, errors, isSubmitting, values }) => (
              <Form>
                <DialogContent dividers>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={8}>
                      <Field
                        as={TextField}
                        label={i18n.t("queueModal.form.name")}
                        autoFocus
                        name="name"
                        fullWidth
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                        variant="outlined"
                        margin="dense"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Field
                        as={TextField}
                        label={i18n.t("queueModal.form.color")}
                        name="color"
                        id="color"
                        fullWidth
                        error={touched.color && Boolean(errors.color)}
                        helperText={touched.color && errors.color}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <div
                                style={{ backgroundColor: values.color }}
                                className={classes.colorAdorment}
                              />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <IconButton
                              size="small"
                              color="default"
                              onClick={() =>
                                setColorPickerModalOpen(!colorPickerModalOpen)
                              }
                            >
                              <Colorize />
                            </IconButton>
                          ),
                        }}
                        variant="outlined"
                        margin="dense"
                      />
                      <ColorBoxModal
                        open={colorPickerModalOpen}
                        handleClose={() => setColorPickerModalOpen(false)}
                        onChange={(color) => {
                          setFieldValue("color", `#${color.hex}`);
                        }}
                        currentColor={values.color}
                      />
                    </Grid>

                    {/* Bloque de Rotación de Agentes */}
                    <Grid item xs={12}>
                      <Box className={classes.rotationBox}>
                        <Typography
                          variant="subtitle2"
                          color="primary"
                          className={classes.rotationTitle}
                        >
                          {i18n.t("queueModal.form.rotationConfig") ||
                            "Configuración de Rotación de Agentes"}
                        </Typography>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={6}>
                            <FormControlLabel
                              control={
                                <Field
                                  as={Switch}
                                  color="primary"
                                  name="ativarRoteador"
                                  checked={values.ativarRoteador}
                                />
                              }
                              label={i18n.t("queueModal.form.rotate")}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormControlLabel
                              control={
                                <Field
                                  as={Checkbox}
                                  color="primary"
                                  name="randomizeImmediate"
                                  checked={values.randomizeImmediate}
                                  disabled={!values.ativarRoteador}
                                />
                              }
                              label={
                                <span>
                                  {i18n.t("queueModal.form.randomizeImmediate")}
                                </span>
                              }
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <FormControl
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              disabled={!values.ativarRoteador}
                            >
                              <InputLabel id="typeRandomMode-selection-label">
                                {i18n.t("queueModal.form.typeRandomMode")}
                              </InputLabel>
                              <Field
                                as={Select}
                                name="typeRandomMode"
                                id="typeRandomMode"
                                labelId="typeRandomMode-selection-label"
                                label={i18n.t("queueModal.form.typeRandomMode")}
                                value={values.typeRandomMode || "RANDOM"}
                              >
                                <MenuItem value="RANDOM">
                                  {i18n.t("queueModal.form.randomMode") ||
                                    "Equitativo / Random"}
                                </MenuItem>
                                <MenuItem value="ORDENADO">
                                  {i18n.t("queueModal.form.orderedMode") ||
                                    "Ordenado"}
                                </MenuItem>
                              </Field>
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <FormControl
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              disabled={!values.ativarRoteador}
                            >
                              <InputLabel id="tempoRoteador-selection-label">
                                {i18n.t("queueModal.form.timeRotate")}
                              </InputLabel>
                              <Field
                                as={Select}
                                name="tempoRoteador"
                                id="tempoRoteador"
                                labelId="tempoRoteador-selection-label"
                                label={i18n.t("queueModal.form.timeRotate")}
                                value={values.tempoRoteador ?? 0}
                              >
                                <MenuItem value={0}>
                                  0 (
                                  {i18n.t("queueModal.form.immediate") ||
                                    "Inmediato"}
                                  )
                                </MenuItem>
                                <MenuItem value={1}>
                                  1 {i18n.t("queueModal.form.minute")}
                                </MenuItem>
                                <MenuItem value={2}>
                                  2 {i18n.t("queueModal.form.minutes")}
                                </MenuItem>
                                <MenuItem value={3}>
                                  3 {i18n.t("queueModal.form.minutes")}
                                </MenuItem>
                                <MenuItem value={4}>
                                  4 {i18n.t("queueModal.form.minutes")}
                                </MenuItem>
                                <MenuItem value={5}>
                                  5 {i18n.t("queueModal.form.minutes")}
                                </MenuItem>
                                <MenuItem value={10}>
                                  10 {i18n.t("queueModal.form.minutes")}
                                </MenuItem>
                                <MenuItem value={15}>
                                  15 {i18n.t("queueModal.form.minutes")}
                                </MenuItem>
                                <MenuItem value={30}>
                                  30 {i18n.t("queueModal.form.minutes")}
                                </MenuItem>
                                <MenuItem value={45}>
                                  45 {i18n.t("queueModal.form.minutes")}
                                </MenuItem>
                                <MenuItem value={60}>
                                  60 {i18n.t("queueModal.form.minutes")}
                                </MenuItem>
                              </Field>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>

                    {/* Mensaje fuera de horario si los horarios están activos */}
                    {schedulesEnabled && (
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          label={i18n.t("queueModal.form.outOfHoursMessage")}
                          multiline
                          minRows={3}
                          fullWidth
                          name="outOfHoursMessage"
                          error={
                            touched.outOfHoursMessage &&
                            Boolean(errors.outOfHoursMessage)
                          }
                          helperText={
                            touched.outOfHoursMessage &&
                            errors.outOfHoursMessage
                          }
                          variant="outlined"
                          margin="dense"
                        />
                      </Grid>
                    )}
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    {i18n.t("queueModal.buttons.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    disabled={isSubmitting}
                    variant="contained"
                    className={classes.btnWrapper}
                  >
                    {queueId
                      ? `${i18n.t("queueModal.buttons.okEdit")}`
                      : `${i18n.t("queueModal.buttons.okAdd")}`}
                    {isSubmitting && (
                      <CircularProgress
                        size={24}
                        className={classes.buttonProgress}
                      />
                    )}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        )}

        {tab === 1 && schedulesEnabled && (
          <Paper style={{ padding: 20 }}>
            <SchedulesForm
              loading={false}
              onSubmit={handleSaveSchedules}
              initialValues={schedules}
              labelSaveButton={i18n.t("whatsappModal.buttons.okAdd")}
            />
          </Paper>
        )}
      </Dialog>
    </div>
  );
};

export default QueueModal;
